/**
 * Site-specific configuration — Santa Barbara.
 */

export const siteConfig = {
  ctx: "sbc",
  defaults: {
    selectedId: '06083000101',
    activeGeography: 'Tract',
    activeIndicator: 'sixtyfive',
    bivariateIndicator: 'prime_age',
    moduleId: 'total-population',
    activeModuleId: 'total-population',
    activeSubCategory: 'Demographics',
    cachedSelectedIds: {
      Boro: '',
      CD: '0643252',
      PUMA: '',
      NTA: '',
      Tract: '06083000101',
    } as Record<string, string>,
    cachedActiveIndicators: {
      Tract: { active: 'sixtyfive', bivariate: 'prime_age' },
      CD: { active: 'sixtyfive', bivariate: 'prime_age' },
      NTA: { active: 'sixtyfive', bivariate: 'prime_age' },
      Boro: { active: 'sixtyfive', bivariate: 'prime_age' },
    } as Record<string, { active: string; bivariate: string }>,
  },

  map: {
    center: [34.71080, -120.01848] as [number, number],
    zoom: 8.75,
    minZoom: 8,
    bounds: [
      [35.867115, -121.380479],
      [33.378189, -119.060989],
    ] as [[number, number], [number, number]],
  },

  geographies: {
    Boro: { enabled: false, label: 'Borough', shortLabel: 'Boro' },
    CD: { enabled: true, label: 'City', shortLabel: 'City' },
    NTA: { enabled: false, label: 'Neighborhood (NTA)', shortLabel: 'Place' },
    Tract: { enabled: true, label: 'Tract', shortLabel: 'Tract' },
    PUMA: { enabled: false, label: 'PUMA', shortLabel: 'PUMA' },
  } as Record<string, { enabled: boolean; label: string; shortLabel: string }>,

  nav: {
    demographicExplorer: false,
    dashboardLabel: 'CITY EXPLORER',
    overallLabel: 'SBC',
  },

  search: {
    geocodeContext: 'Santa Barbara County, CA',
    geocodeBounds: {
      minLon: -121.380479,
      minLat: 33.378189,
      maxLon: -119.060989,
      maxLat: 35.867115,
    },
  },

  data: {
    sorter: {
      path: '/data/tabular/00_SB_Pilot/SORTER_Stat_Explorer.csv',
      suffixMap: {} as Record<string, string>,
      fieldMap: { SBC: 'NYC', Place: 'CD' } as Record<string, string>,
    },
    order: {
      path: '/data/tabular/00_SB_Pilot/ORDER_Stat_Explorer.csv',
    },
    orderStories: {
      path: '/data/tabular/00_SB_Pilot/ORDER_Data_Stories.csv',
    },
    orderDashboard: {
      path: '/data/tabular/00_SB_Pilot/ORDER_City_Explorer.csv',
    },
    sorterDashboard: {
      path: '/data/tabular/00_SB_Pilot/SORTER_City_Explorer.csv',
      suffixMap: {} as Record<string, string>,
      fieldMap: { SBC: 'NYC', Place: 'CD' } as Record<string, string>,
    },
    tract: {
      path: '/data/tabular/00_SB_Pilot/Tract.csv',
      suffixMap: {} as Record<string, string>,
      excludedGeoids: ['06083980100', '06083990000'], // 06083990000 exists in .csv tabular data, but not the TopoJSON.
    },
    nyc: {
      path: '/data/tabular/00_SB_Pilot/County.csv',
      suffixMap: { '_county': '_nyc' } as Record<string, string>,
    },
    cd: {
      path: '/data/tabular/00_SB_Pilot/Place.csv',
      suffixMap: { '_place': '_cd' } as Record<string, string>,
    },
    topoJson: '/data/geo/ALL_GEO_2020_SB_raw.json',
    geoidLookup: false,
    geoTypeRemap: { place: 'cd', county: 'nyc' } as Record<string, string>,
    tooltipNameOverrides: {
      'Vandenberg Space Force Base': 'Vandenberg SFB',
    } as Record<string, string>,
  },

  swarmCalibration: {
    Tract: {
      radius: 4,
      padding: 0.25,
      pointDiameter: 0.75,
      collisionDiameter: 1.5,
      stackCount: 36,
      testEachPoint: "FALSE",
    },
    CD: {
      radius: 6,
      padding: 0.5,
      pointDiameter: 2,
      collisionDiameter: 1.3,
      stackCount: 45,
      testEachPoint: "FALSE",
    },
    CDRG: {
      radius: 1.5,
      padding: 1.5,
      pointDiameter: 2,
      collisionDiameter: 0.75,
      stackCount: 25,
      testEachPoint: "FALSE",
    },
    Boro: {
      radius: 12,
      padding: 0.5,
      pointDiameter: 2,
      collisionDiameter: 3,
      stackCount: 3,
      testEachPoint: "FALSE",
    },
    BoroRG: {
      radius: (10 * .75) / 2,
      padding: 0.5,
      pointDiameter: 2,
      collisionDiameter: 1.7,
      stackCount: 3,
      testEachPoint: "FALSE",
    },
    NTA: {
      radius: 4,
      padding: 0.5,
      pointDiameter: 2,
      collisionDiameter: 0.5,
      stackCount: 25,
      testEachPoint: "FALSE",
    },
    ScatterPlot: {
      radius: 3,
    },
    RangeSwarm: {
      Tract: {
        radius: 2,
        padding: 0.25,
        pointDiameter: 0.75,
        collisionDiameter: 1.65,
        intervalBufferPct: 1.5,
        rowSpacing: 1,
        stackCount: 45,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
      CD: {
        radius: 3,
        padding: 0.5,
        pointDiameter: 2,
        collisionDiameter: 2,
        intervalBufferPct: 1.5,
        rowSpacing: 1.5,
        stackCount: 45,
        testEachPoint: "FALSE",
        segmentLengthPct: 4,
      },
      CDRG: {
        radius: 1.5,
        padding: 1.5,
        pointDiameter: 2,
        collisionDiameter: 0.8,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 25,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
      Boro: {
        radius: 12,
        padding: 0.5,
        pointDiameter: 2,
        collisionDiameter: 3.25,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 3,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
      BoroRG: {
        radius: (10 * .75) / 2,
        padding: 0.5,
        pointDiameter: 2,
        collisionDiameter: 1.8,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 3,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
      NTA: {
        radius: 4,
        padding: 0.5,
        pointDiameter: 2,
        collisionDiameter: 0.55,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 25,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
    },
  } as Record<string, any>,

  mobileIndicators:[
    "sixtyfive",
    "prime_age",
    "undereighteen",
    "underfive",
    "health_insurance_direct",
    "health_insurance_employer",
    "health_insurance_multiple",
    "health_insurance_public"
  ],

  mobileDisplays: {
    sixtyfive : "Population 65+",
    prime_age : "Prime Age Population",
    undereighteen : "Population Under 18",
    underfive : "Population Under 5",
    health_insurance_direct : "Direct Health Insurance",
    health_insurance_employer : "Employer Health Insurance",
    health_insurance_multiple : "Multiple Health Insurance",
    health_insurance_public : "Public Health Insurance",
  },

  storyTabs: [
    { key: 'Well-Being in NYC', label: 'Well-Being in SB County', type: 'interactive' },
    { key: 'Housing in SB County', label: 'Housing in SB County', type: 'interactive' },
    { key: 'SB Test Story 3', label: 'SB Test Story 3', type: 'interactive' },
  ] as { key: string; label: string; type: 'interactive' | 'pdf'; url?: string }[],

  defaultStory: 'Well-Being in NYC',
};
